import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

/**
 * Shared admin UI kit — mirrors the public site's polish (Section/Account):
 * one animated page header, stagger/rise variants, and a Reveal wrapper so
 * every admin screen fades in with the same rhythm the frontend uses.
 */

const EASE = [0.22, 0.6, 0.36, 1] as const;

export const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } };
export const rise = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } } };

/** Consistent page header: icon chip + title + subtitle, actions slot on the right. */
export function AdminHeader({ icon: Icon, title, subtitle, actions }: {
  icon?: any;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE }}
      className="flex flex-wrap items-end justify-between gap-4 mb-6"
    >
      <div className="min-w-0">
        <h1 className="h-title text-3xl font-extrabold flex items-center gap-3">
          {Icon && (
            <span className="w-9 h-9 rounded-xl grid place-items-center bg-[color:var(--color-brand)]/15 border border-[color:var(--color-brand)]/25 text-[color:var(--color-brand-glow)] shrink-0">
              <Icon width={18} height={18} />
            </span>
          )}
          {title}
        </h1>
        {subtitle && <p className="text-sm text-[color:var(--color-ink-dim)] mt-1.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </motion.div>
  );
}

/** Fade-up wrapper. Pass `i` for a staggered delay across a list. */
export function Reveal({ children, i = 0, className = '' }: { children: ReactNode; i?: number; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: i * 0.04, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
